import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

// Components
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  SelectCustom,
  TextInputCustom,
} from '../../../../atoms';
import { DeleteIcon } from '../../../../../assets/icons/delete-icon';
import { FieldFechaNacimiento } from './Field-fecha-nacimiento';
import { cn } from '../../../../../lib';

// Constants
import {
  parentescoArray,
  tiposIdentificaciones,
  // GENEROS,
} from '../../../../../common/constants';
import { defauldValueDependiente } from '../constants';
import { useGetPersonas } from '../query';
import { GENEROS } from '../../../../../common/constants/const';
import { typesValidation } from '../../../../../common/types';

export function FieldFormDependiente({ isPasoEmision = false }) {
  const form = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'dependientes',
  });

  return (
    <div className="flex flex-col gap-7 transition-all duration-700">
      {fields.map((field, index) => (
        <DependienteCard
          key={field.id}
          index={index}
          remove={remove}
          isPasoEmision={isPasoEmision}
        />
      ))}

      {!isPasoEmision && fields.length < 7 && (
        <CardAddDependiente append={() => append(defauldValueDependiente)} />
      )}
    </div>
  );
}

function DependienteCard({ index, remove, isPasoEmision = false }) {
  const form = useFormContext();

  const identificacion = form.watch(
    `dependientes.${index}.numeroIdentificacion`,
  );

  const [open, setOpen] = useState(false);
  const [showInput, setShowInput] = useState(identificacion.length === 9);

  const tipoIdentificacion = form.watch(
    `dependientes.${index}.tipoIdentificacion`,
  );

  const { refetch, isFetching } = useGetPersonas(
    { identificacion, tipo: tipoIdentificacion },
    data => {
      form.setValue(
        `dependientes.${index}.nombreCompleto`,
        `${data?.data?.primerNombre ?? ''} ${data?.data?.segundoNombre ?? ''} ${data?.data?.primerApellido ?? ''} ${data?.data?.segundoApellido ?? ''}`.trim(),
      );

      if (!isPasoEmision) {
        form.setValue(
          `dependientes.${index}.fechaNacimiento`,
          data?.data?.fechaNacimiento || '',
        );
      }

      setShowInput(true);
    },
  );

  const parentescoGeneroMap = {
    '040': 'Femenino',
    '050': 'Masculino',
  };

  const valueParentesco = form.watch(`dependientes.${index}.parentesco`);
  const setGenero = parentescoGeneroMap[valueParentesco];

  if(setGenero){
    form.setValue(`dependientes.${index}.genero`, setGenero);
  }

  return (
    <div
      className={cn(
        'rounded-md shadow-xl flex flex-col gap-3 p-4 lg:px-9',
        !open && 'pb-10',
      )}
    >
      <HeaderCard
        open={open}
        setOpen={setOpen}
        index={index + 1}
        remove={() => remove(index)}
        isPasoEmision={isPasoEmision}
      />
      <div
        className={cn(
          'grid lg:grid-cols-2 gap-4 border-t pt-4',
          open && 'hidden',
        )}
      >
        <FieldSelector
          name={`dependientes.${index}.tipoIdentificacion`}
          label="Tipo de identificación"
          options={tiposIdentificaciones}
        />
        <FieldInput
          name={`dependientes.${index}.numeroIdentificacion`}
          label="Número de identidad"
          onKeyUp={() => identificacion?.length === 9 && refetch()}
          isFetching={isFetching}
          typesValidationProps={typesValidation.multiLettersExtend}
        />
        {showInput && (
          <>
            <FieldInput
              name={`dependientes.${index}.nombreCompleto`}
              label="Nombre completo"
              maxLength={255}
            />
            <FieldFechaNacimiento index={index} isPasoEmision={isPasoEmision} />
            <FieldSelector
              name={`dependientes.${index}.genero`}
              label="Género"
              options={GENEROS}
            />
            <FieldSelector
              name={`dependientes.${index}.parentesco`}
              label="Parentesco"
              options={parentescoArray}
            />
          </>
        )}
      </div>
    </div>
  );
}

function HeaderCard({ open, setOpen, index, remove, isPasoEmision = false }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xl font-semibold text-primary flex-shrink-0">
        Dependiente {index}
      </span>
      <div
        className={cn(
          'grid grid-cols-2 md:gap-4 items-center text-sm md:text-base flex-shrink-0',
          isPasoEmision && 'grid-cols-1',
        )}
      >
        <span
          onClick={() => setOpen(!open)}
          className="cursor-pointer text-info font-medium text-right"
        >
          {open ? '+ Ver más' : '- Ver menos'}
        </span>
        {!isPasoEmision && (
          <div
            className="p-1.5 rounded-md gap-1 flex items-center text-danger cursor-pointer"
            onClick={remove}
          >
            <DeleteIcon className="size-[15px]" />
            <span>Eliminar</span>
          </div>
        )}
      </div>
    </div>
  );
}

function CardAddDependiente({ append }) {
  return (
    <div
      className="rounded-md shadow-xl flex flex-col gap-3 p-5 lg:px-9 cursor-pointer"
      onClick={append}
    >
      <span className="text-xl font-semibold text-primary">
        + Agregar Dependiente
      </span>
    </div>
  );
}

function FieldSelector({ name, label, options }) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label} <span className="text-danger">*</span>
          </FormLabel>
          <FormControl>
            <SelectCustom
              options={options}
              value={field.value}
              setValue={field.onChange}
              placeholder={`Ingresa ${label.toLowerCase()}`}
              borderStyle={true}
              alinear="izquierda"
              msgError={form.getFieldState(name).error?.message}
              {...field}
              name=""
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

function FieldInput({
  name,
  label,
  onKeyUp,
  isFetching,
  typesValidationProps = typesValidation.lettersExtend,
  maxLength = 9,
}) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {label} <span className="text-danger">*</span>
          </FormLabel>
          <FormControl>
            <TextInputCustom
              placeholder={`Ingresa ${label.toLowerCase()}`}
              borderStyle={true}
              msgError={form.getFieldState(name).error?.message}
              alignError="left"
              {...field}
              name=""
              onKeyUp={onKeyUp}
              maxLength={maxLength}
              loader={isFetching}
              typesValidation={typesValidationProps}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}
